import { ShieldCheck, Upload } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import { Tooltip } from '@/components/ui/Tooltip'
import { StatusChip, type VideoStatus } from '@/components/ds/StatusChip'
import styles from './ProjectHeader.module.scss'

/** Backend's safety status string. The known values get distinct chip
 * variants; anything else (legacy snapshots) falls through to a neutral
 * default so the UI never crashes on a status we forgot to enumerate. */
export type SafetyStatus = string

export interface ProjectHeaderProps {
  eyebrow: ReactNode
  title: string
  status: VideoStatus
  factoryLabel: string
  language: string
  aspect?: string
  onPublish?: () => void
  /** Pre-publish gate. ``passed`` → button hides; anything else → shows
   * "Verificar segurança" so the user can rescan without diving into the
   * upload modal failure path. */
  safetyStatus?: SafetyStatus | null
  onRescanSafety?: () => void
  rescanRunning?: boolean
  /** When ``status === 'agendado'``, override the chip label (e.g. "Agendado · 14/05 17h"). */
  scheduledLabel?: string | null
  /** Tooltip body for the scheduled chip — full date + relative countdown. */
  scheduledTooltip?: string | null
  /** Slot for the "more actions" control. Caller usually passes a
   * `<DropdownMenuRoot>` whose trigger is an `IconButton` — keeps the
   * header unaware of the menu's content. */
  moreSlot?: ReactNode
}

/**
 * Head section of the Project Detail page — eyebrow + display title + a row
 * of chips (status, factory, lang/aspect, duration) on the left, with
 * action buttons on the right. Actions are optional; stub when the
 * matching delivery hasn't landed.
 */
export function ProjectHeader({
  eyebrow,
  title,
  status,
  factoryLabel,
  language,
  aspect = '9:16',
  onPublish,
  safetyStatus,
  onRescanSafety,
  rescanRunning = false,
  scheduledLabel,
  scheduledTooltip,
  moreSlot,
}: ProjectHeaderProps) {
  const safetyChip = safetyChipFor(safetyStatus)
  // Show the rescan CTA while the gate is anything other than "passed".
  // Once it passes the user has no reason to re-run it manually; if they
  // need to (e.g. after editing the script) they can always go through the
  // ProjectActions menu.
  const needsRescan = Boolean(onRescanSafety) && safetyStatus !== 'passed'
  const statusChipNode = scheduledLabel
    ? <StatusChip status={status} label={scheduledLabel} />
    : <StatusChip status={status} />

  return (
    <header className={styles.header}>
      <div className={styles.text}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.chips}>
          {scheduledTooltip ? (
            <Tooltip content={scheduledTooltip}>
              <span>{statusChipNode}</span>
            </Tooltip>
          ) : (
            statusChipNode
          )}
          <Chip>{factoryLabel}</Chip>
          <Chip>{`${language} · ${aspect}`}</Chip>
          {safetyChip ? (
            <Tooltip content={safetyChip.tooltip}>
              <Chip variant={safetyChip.variant}>{safetyChip.label}</Chip>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div className={styles.actions}>
        {moreSlot}
        {needsRescan ? (
          <Button
            variant="secondary"
            leadingIcon={<ShieldCheck />}
            onClick={onRescanSafety}
            disabled={rescanRunning}
          >
            {rescanRunning ? 'Verificando...' : 'Verificar segurança'}
          </Button>
        ) : null}
        <Button leadingIcon={<Upload />} onClick={onPublish}>
          Publicar
        </Button>
      </div>
    </header>
  )
}

interface SafetyChip {
  label: string
  variant: ChipVariant
  /** Hover/focus copy. Always names the safety filter explicitly so users
   * connect the chip to the "Verificar segurança" action. */
  tooltip: string
}

function safetyChipFor(status: SafetyStatus | null | undefined): SafetyChip | null {
  if (!status) return null
  switch (status) {
    case 'passed':
      return {
        label: 'Seguro',
        variant: 'success',
        tooltip: 'Filtro de segurança: passou em todas as camadas e está liberado para upload.',
      }
    case 'blocked':
      return {
        label: 'Bloqueado',
        variant: 'danger',
        tooltip:
          'Filtro de segurança: o vídeo foi reprovado e não pode ser publicado. Veja as categorias para entender por quê.',
      }
    case 'error':
      return {
        label: 'Erro no scan',
        variant: 'danger',
        tooltip:
          'Filtro de segurança: alguma camada de verificação falhou. Rode "Verificar segurança" novamente.',
      }
    case 'pending':
      return {
        label: 'Verificando',
        variant: 'warning',
        tooltip: 'Filtro de segurança em execução — aguarde o job terminar.',
      }
    case 'never_checked':
      return {
        label: 'Sem scan',
        variant: 'warning',
        tooltip:
          'Filtro de segurança ainda não rodou neste vídeo. Use "Verificar segurança" antes de publicar.',
      }
    default:
      return {
        label: status,
        variant: 'default',
        tooltip: `Status do filtro de segurança: ${status}.`,
      }
  }
}
